import { Data, Layout } from "plotly.js";
import { useMemo, useState } from "react";
import Plot from "react-plotly.js";
import { BACKGROUND_COLORS } from "../../constants/design/colors";
import { IAccountBalance } from "../../hooks/api/mm/useAccountBalanceHistory";
import { ChartContainer, ToggleButton, ToggleContainer } from "./style";

interface IEquityChartProps {
  strategy: string;
  data: IAccountBalance[];
}

// Color palette for different strategies and accounts
const COLORS = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#17becf",
  "#aec7e8", "#ffbb78", "#98df8a", "#ff9896", "#c5b0d5",
];

function EquityChart({ strategy, data }: IEquityChartProps) {
  const [showAggregate, setShowAggregate] = useState(true);

  const layout = useMemo(
    () =>
      ({
        title: `${strategy.charAt(0).toUpperCase() + strategy.slice(1).replace(/_/g, " ")} Strategy Equity`,
        xaxis: {
          autorange: true,
          type: "date",
          title: "Date",
        },
        yaxis: {
          autorange: true,
          title: "USD Balance",
          tickformat: ",.0f",
        },
        plot_bgcolor: BACKGROUND_COLORS.five,
        paper_bgcolor: "transparent",
        font: {
          color: "white",
        },
        hovermode: "closest",
        showlegend: true,
        legend: {
          orientation: "v",
          x: 1.02,
          y: 1,
        },
      } as Layout),
    [strategy]
  );

  const plotData = useMemo(() => {
    if (!data || data.length === 0) {
      return [] as Data[];
    }

    if (showAggregate) {
      // Aggregate view: sum all accounts per timestamp
      const timestampMap = new Map<string, number>();

      data.forEach((balance) => {
        const timestamp = balance.Timestamp;
        const usdBalance = parseFloat(balance.USDBalance);
        const current = timestampMap.get(timestamp) || 0;
        timestampMap.set(timestamp, current + usdBalance);
      });

      // Sort by timestamp
      const sortedEntries = Array.from(timestampMap.entries()).sort(
        ([a], [b]) => Number(a) - Number(b)
      );

      return [
        {
          x: sortedEntries.map(([timestamp]) =>
            new Date(Number(timestamp) / 1e6).toISOString()
          ),
          y: sortedEntries.map(([, balance]) => balance),
          type: "scatter",
          mode: "lines+markers",
          line: {
            color: COLORS[0],
            width: 2,
          },
          marker: {
            size: 4,
          },
          name: `Total ${strategy}`,
          hovertemplate: "<b>%{y:,.2f} USD</b><br>%{x}<extra></extra>",
        },
      ] as Data[];
    }
      // Individual view: separate lines for each Exchange+AccountName
      const accountMap = new Map<string, { timestamps: string[]; balances: number[] }>();

      data.forEach((balance) => {
        const key = `${balance.Exchange} - ${balance.AccountName}`;
        if (!accountMap.has(key)) {
          accountMap.set(key, { timestamps: [], balances: [] });
        }
        const account = accountMap.get(key)!;
        account.timestamps.push(balance.Timestamp);
        account.balances.push(parseFloat(balance.USDBalance));
      });

      // Sort each account's data by timestamp
      accountMap.forEach((account, key) => {
        const combined = account.timestamps
          .map((timestamp, i) => ({ timestamp, balance: account.balances[i] }))
          .sort((a, b) => Number(a.timestamp) - Number(b.timestamp));

        const sortedTimestamps = combined.map((item) => item.timestamp);
        const sortedBalances = combined.map((item) => item.balance);
        accountMap.set(key, { timestamps: sortedTimestamps, balances: sortedBalances });
      });

      const traces: Data[] = [];
      let colorIndex = 0;

      accountMap.forEach((account, key) => {
        traces.push({
          x: account.timestamps.map((timestamp) =>
            new Date(Number(timestamp) / 1e6).toISOString()
          ),
          y: account.balances,
          type: "scatter",
          mode: "lines+markers",
          line: {
            color: COLORS[colorIndex % COLORS.length],
            width: 2,
          },
          marker: {
            size: 3,
          },
          name: key,
          hovertemplate: `<b>${key}</b><br>%{y:,.2f} USD<br>%{x}<extra></extra>`,
        });
        colorIndex += 1;
      });

      return traces as Data[];
  }, [data, showAggregate, strategy]);

  return (
    <div style={{ marginBottom: "40px" }}>
      <ToggleContainer>
        <ToggleButton
          active={showAggregate}
          onClick={() => setShowAggregate(true)}
        >
          Aggregate
        </ToggleButton>
        <ToggleButton
          active={!showAggregate}
          onClick={() => setShowAggregate(false)}
        >
          Individual
        </ToggleButton>
      </ToggleContainer>
      <ChartContainer>
        {data.length === 0 ? (
          <div>No data available for {strategy}</div>
        ) : (
          <Plot
            data={plotData}
            layout={layout}
            useResizeHandler
            style={{ width: "100%", height: "500px" }}
          />
        )}
      </ChartContainer>
    </div>
  );
}

export default EquityChart;
